import { clampNormalized } from "@/lib/planViewer";
import type { SubLocation } from "@/lib/wartungen";

export const PLAN_ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/tiff",
]);
export const PLAN_ALLOWED_EXTENSIONS = new Set(["pdf", "jpg", "jpeg", "png", "webp", "tif", "tiff"]);
export const PLAN_ZOOM_MIN = 0.5;
export const PLAN_ZOOM_MAX = 6;
export const PLAN_ZOOM_STEP = 0.25;

export interface PlanPoint {
  asset_id?: number | null;
  x?: number | null;
  y?: number | null;
  label_x?: number | null;
  label_y?: number | null;
  label_text?: string;
  coordinate_mode?: string;
  page_number?: number;
}

export interface AssetPlanPosition {
  plan_id?: number | null;
  plan_title?: string;
  plan_page?: number | null;
  plan_point?: {
    x?: number | null;
    y?: number | null;
    label_x?: number | null;
    label_y?: number | null;
    coordinate_mode?: string;
  } | null;
  label_text?: string;
  label_position?: { x?: number | null; y?: number | null; coordinate_mode?: string } | null;
  is_positioned?: boolean;
}

export interface HuelleAsset {
  asset_id?: number;
  external_id?: string | null;
  name?: string | null;
  status?: string | null;
  location_label?: string | null;
  location_path?: string[];
  sub_location_id?: number | null;
  last_performed_at?: string | null;
  is_plan_positioned?: boolean;
  plan_positions?: AssetPlanPosition[];
  plan_id?: number | null;
  plan_title?: string;
  plan_page?: number | null;
  plan_point?: AssetPlanPosition["plan_point"];
  label_text?: string;
  label_position?: AssetPlanPosition["label_position"];
  plan_label_symbol?: {
    svg_markup?: string;
    svgMarkup?: string;
    png_preview_base64?: string;
    pngPreviewBase64?: string;
  } | null;
  [k: string]: unknown;
}

export interface MaintenancePlanRecord {
  id?: number | null;
  page?: number;
  page_number?: number;
  title?: string;
  version?: string;
  description?: string;
  filename?: string;
  file_size_bytes?: number;
  is_active?: boolean;
  source_file_type?: string;
  rendered_plan_image_url?: string;
  preview_url?: string;
  resolved_plan_image_url?: string;
  file_proxy_url?: string;
  marker_overlay_scale?: number;
  has_legacy_plan?: boolean;
  legacy_plan_filename?: string;
  legacy_plan_file_size_bytes?: number;
  legacy_plan_page?: number;
  legacy_plan_page_number?: number;
  legacy_plan_source_file_type?: string;
  legacy_plan_file_proxy_url?: string;
  legacy_plan_rendered_plan_image_url?: string;
  legacy_plan_preview_url?: string;
  has_fire_safety_plan?: boolean;
  fire_safety_plan_filename?: string;
  fire_safety_plan_file_size_bytes?: number;
  fire_safety_plan_page?: number;
  fire_safety_plan_page_number?: number;
  fire_safety_plan_source_file_type?: string;
  fire_safety_plan_file_proxy_url?: string;
  fire_safety_plan_rendered_plan_image_url?: string;
  fire_safety_plan_preview_url?: string;
  points?: PlanPoint[];
  [k: string]: unknown;
}

export function toPathSegments(entry: SubLocation | null | undefined): string[] {
  if (Array.isArray(entry?.path_segments) && entry.path_segments.length > 0) {
    return entry.path_segments.map((segment) => String(segment || "").trim()).filter(Boolean);
  }
  return String(entry?.path_label || "")
    .split(" > ")
    .map((segment) => segment.trim())
    .filter(Boolean);
}

export function pathKeyFromSegments(pathSegments: string[]): string {
  return JSON.stringify(
    Array.isArray(pathSegments) ? pathSegments.map((segment) => String(segment || "").trim()) : [],
  );
}

export function buildSubLocationPathIndex(subLocations: SubLocation[]) {
  const idByPathKey = new Map<string, number>();
  const pathById = new Map<number, string[]>();
  for (const entry of subLocations) {
    const subLocationId = Number(entry?.id);
    if (!Number.isFinite(subLocationId) || subLocationId <= 0) continue;
    const pathSegments = toPathSegments(entry);
    if (pathSegments.length === 0) continue;
    idByPathKey.set(pathKeyFromSegments(pathSegments), subLocationId);
    pathById.set(subLocationId, pathSegments);
  }
  return { idByPathKey, pathById };
}

export function clampMarkerOverlayScale(value: unknown): number {
  const n = Number(value);
  if (!Number.isFinite(n)) return 1;
  return Math.min(4, Math.max(0.25, n));
}

export function buildRenderedPlanImageUrl(plan: MaintenancePlanRecord | null, pageNumber: number): string {
  const base = String(plan?.rendered_plan_image_url || plan?.preview_url || "").trim();
  if (!base) return "";
  try {
    const url = new URL(base, window.location.origin);
    url.searchParams.set("page_number", String(Number(pageNumber) || 1));
    return url.origin === window.location.origin
      ? `${url.pathname}${url.search}${url.hash}`
      : url.toString();
  } catch {
    const withoutPage = base.replace(/([?&])page_number=\d+/i, "$1").replace(/[?&]$/, "");
    return `${withoutPage}${withoutPage.includes("?") ? "&" : "?"}page_number=${Number(pageNumber) || 1}`;
  }
}

export function normalizePlanPointRecord(
  point: PlanPoint | null | undefined,
  plan: MaintenancePlanRecord | null,
): PlanPoint | null {
  if (!point || typeof point !== "object") return null;
  return {
    ...point,
    asset_id: Number(point.asset_id) || null,
    page_number: Number(point.page_number || plan?.page_number || 1) || 1,
    label_text: String(point.label_text || "").trim(),
  };
}

export function normalizeAssetPlanPosition(position: AssetPlanPosition | null | undefined): AssetPlanPosition | null {
  if (!position || typeof position !== "object") return null;
  const normalizedPlanId = Number(position.plan_id);
  const normalizedPlanPage = Number(position.plan_page);
  const planPoint = position.plan_point
    ? {
        x: clampNormalized(position.plan_point.x),
        y: clampNormalized(position.plan_point.y),
        label_x: clampNormalized(position.plan_point.label_x),
        label_y: clampNormalized(position.plan_point.label_y),
        coordinate_mode:
          String(position.plan_point.coordinate_mode || "normalized").trim().toLowerCase() || "normalized",
      }
    : null;
  const labelPosition = position.label_position
    ? {
        x: clampNormalized(position.label_position.x),
        y: clampNormalized(position.label_position.y),
        coordinate_mode:
          String(position.label_position.coordinate_mode || "normalized").trim().toLowerCase() || "normalized",
      }
    : null;
  return {
    ...position,
    plan_id: Number.isFinite(normalizedPlanId) && normalizedPlanId > 0 ? normalizedPlanId : null,
    plan_title: String(position.plan_title || "").trim(),
    plan_page: Number.isFinite(normalizedPlanPage) && normalizedPlanPage > 0 ? normalizedPlanPage : null,
    plan_point: planPoint,
    label_text: String(position.label_text || "").trim(),
    label_position: labelPosition,
    is_positioned: Boolean(planPoint && planPoint.x !== null && planPoint.y !== null),
  };
}

export function normalizeAssetRecord(asset: HuelleAsset | null | undefined): HuelleAsset | null {
  if (!asset || typeof asset !== "object") return null;
  const assetId = Number(asset.asset_id);
  if (!Number.isFinite(assetId) || assetId <= 0) return null;
  const normalizedPlanPositions = (Array.isArray(asset.plan_positions) ? asset.plan_positions : [])
    .map(normalizeAssetPlanPosition)
    .filter(Boolean) as AssetPlanPosition[];
  const fallbackSinglePosition =
    !normalizedPlanPositions.length && asset.plan_id
      ? normalizeAssetPlanPosition({
          plan_id: asset.plan_id,
          plan_title: asset.plan_title,
          plan_page: asset.plan_page,
          plan_point: asset.plan_point,
          label_text: asset.label_text,
          label_position: asset.label_position,
        })
      : null;
  const planPositions = fallbackSinglePosition ? [fallbackSinglePosition] : normalizedPlanPositions;
  const primaryPlanPosition = planPositions.length === 1 ? planPositions[0] : null;
  return {
    ...asset,
    asset_id: assetId,
    external_id: String(asset.external_id || "").trim(),
    name: String(asset.name || "").trim(),
    location_path: Array.isArray(asset.location_path)
      ? asset.location_path.map((segment) => String(segment || "").trim()).filter(Boolean)
      : [],
    location_label: String(asset.location_label || "").trim(),
    plan_positions: planPositions,
    plan_id: primaryPlanPosition?.plan_id ?? null,
    plan_title: primaryPlanPosition?.plan_title || "",
    plan_page: primaryPlanPosition?.plan_page ?? null,
    plan_point: primaryPlanPosition?.plan_point || null,
    label_text: primaryPlanPosition?.label_text || "",
    label_position: primaryPlanPosition?.label_position || null,
    is_plan_positioned: planPositions.some((position) => position?.is_positioned),
  };
}

export function normalizePlanRecord(plan: MaintenancePlanRecord | null | undefined): MaintenancePlanRecord | null {
  if (!plan || typeof plan !== "object") return null;
  const pageNumber = Number(plan.page_number || plan.page || 1) || 1;
  return {
    ...plan,
    id: Number(plan.id) || null,
    page: Number(plan.page || pageNumber) || pageNumber,
    page_number: pageNumber,
    title: String(plan.title || "").trim(),
    version: String(plan.version || "").trim(),
    description: String(plan.description || "").trim(),
    filename: String(plan.filename || "").trim(),
    file_size_bytes: Number(plan.file_size_bytes || 0) || 0,
    is_active: Boolean(plan.is_active),
    source_file_type: String(plan.source_file_type || plan.file_type || "")
      .trim()
      .toLowerCase(),
    rendered_plan_image_url: String(plan.rendered_plan_image_url || plan.preview_url || "").trim(),
    resolved_plan_image_url: buildRenderedPlanImageUrl(plan, pageNumber),
    marker_overlay_scale: clampMarkerOverlayScale(plan.marker_overlay_scale),
    points: (Array.isArray(plan.points) ? plan.points : [])
      .map((point) => normalizePlanPointRecord(point, plan))
      .filter(Boolean) as PlanPoint[],
  };
}

export function formatFileSize(bytes: unknown): string {
  const size = Number(bytes);
  if (!Number.isFinite(size) || size <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let index = 0;
  while (value >= 1024 && index < units.length - 1) {
    value /= 1024;
    index += 1;
  }
  return `${value >= 10 || index === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[index]}`;
}

export function formatPlanSummary(plan: MaintenancePlanRecord | null): string {
  if (!plan) return "Noch kein Plan gewählt.";
  const parts = [
    plan.title || plan.filename || `Plan ${plan.id}`,
    plan.version ? `Version ${plan.version}` : "",
    plan.page ? `Seite ${plan.page}` : "",
    plan.is_active ? "aktiv" : "inaktiv",
    formatFileSize(plan.file_size_bytes),
  ].filter(Boolean);
  return parts.join(" · ");
}

export function isAllowedPlanFile(file: File): boolean {
  const mime = String(file.type || "").trim().toLowerCase();
  const ext = String(file.name || "").split(".").pop()?.trim().toLowerCase() || "";
  return PLAN_ALLOWED_MIME_TYPES.has(mime) || PLAN_ALLOWED_EXTENSIONS.has(ext);
}

export function dedupeAssetsById(items: HuelleAsset[]): HuelleAsset[] {
  const mergedById = new Map<number, HuelleAsset>();
  for (const rawItem of Array.isArray(items) ? items : []) {
    const item = normalizeAssetRecord(rawItem);
    if (!item?.asset_id) continue;
    const prev = mergedById.get(item.asset_id);
    if (!prev) {
      mergedById.set(item.asset_id, item);
      continue;
    }
    const sym = prev.plan_label_symbol || rawItem.plan_label_symbol;
    mergedById.set(item.asset_id, { ...prev, ...item, ...(sym ? { plan_label_symbol: sym } : {}) });
  }
  return Array.from(mergedById.values());
}

export interface TreeNode {
  label: string;
  path: string[];
  children: Map<string, TreeNode>;
}

export function buildLocationTree(paths: string[][]): TreeNode {
  const root: TreeNode = { label: "Basisort", path: [], children: new Map() };
  for (const path of paths) {
    let node = root;
    const trail: string[] = [];
    for (const segment of path) {
      const key = String(segment || "");
      trail.push(key);
      if (!node.children.has(key)) {
        node.children.set(key, { label: key, path: [...trail], children: new Map() });
      }
      node = node.children.get(key)!;
    }
  }
  return root;
}

export function resolveAssetPath(
  asset: HuelleAsset,
  pathById: Map<number, string[]>,
): string[] {
  const directPath = Array.isArray(asset.location_path)
    ? asset.location_path.map((segment) => String(segment || "").trim()).filter(Boolean)
    : [];
  if (directPath.length > 0) return directPath;
  const subLocationId = Number(asset.sub_location_id);
  if (Number.isFinite(subLocationId) && subLocationId > 0) {
    const mappedPath = pathById.get(subLocationId);
    if (Array.isArray(mappedPath) && mappedPath.length > 0) return mappedPath;
  }
  return [];
}

export function pointValue(point: PlanPoint | null, kind: "object" | "label") {
  if (!point) return null;
  if (kind === "object") {
    const x = clampNormalized(point.x);
    const y = clampNormalized(point.y);
    return x !== null && y !== null ? { x, y } : null;
  }
  const x = clampNormalized(point.label_x);
  const y = clampNormalized(point.label_y);
  return x !== null && y !== null ? { x, y } : null;
}

export function denormalizePlanPoint(
  point: { x: number; y: number } | null,
  imageWidth: number,
  imageHeight: number,
) {
  if (!point) return null;
  const x = clampNormalized(point.x);
  const y = clampNormalized(point.y);
  if (x === null || y === null) return null;
  return {
    x: x * Math.max(1, imageWidth),
    y: y * Math.max(1, imageHeight),
  };
}

export function buildPlanLabelText(point: PlanPoint | null, asset?: HuelleAsset | null): string {
  const fromPoint = String(point?.label_text || "").trim();
  if (fromPoint) return fromPoint;
  if (!asset) return "";
  return String(asset.external_id || asset.name || "").trim();
}

export function getAssetDefaultLabelText(asset: HuelleAsset | null | undefined): string {
  if (!asset) return "";
  return String(asset.external_id || asset.name || "").trim();
}
